import * as Sentry from "@sentry/react";
import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { useAudit } from "../contexts/auditContext";
import { controlsV2SessionApiBase } from "../lib/sessionApiBase";
import { requestPresignedUrls, uploadFileToS3 } from "../api/presignedUpload";
import {
  EvidenceRunFailedError,
  EvidenceRunTimeoutError,
  pollEvidenceConfirm,
  pollEvidencePreview,
} from "../lib/evidenceRunPolling";
import {
  EvidenceConfirmDispatchSchema,
  EvidenceParseDispatchSchema,
  EvidenceUploadPreviewSchema,
  type EvidenceUploadPreview,
  type EvidenceRunState,
  type ZipOrganizationPattern,
  type ConfirmSampleMapping,
  type ConfirmDocTypeMapping,
} from "../models/audit/evidenceUpload";

interface UseEvidenceUploadOptions {
  controlSessionId: string;
}

/**
 * Outcome of parsing a zip.
 *
 * A bare `null` carried no reason, so the modal always showed "the Midship
 * team has been informed" - untrue for a 400, which is the auditor's to fix.
 * `reason` is set only when the backend rejected the archive; a genuine fault
 * leaves it null, because that one really is ours.
 */
export type ParseZipOutcome =
  | { ok: true; preview: EvidenceUploadPreview }
  | { ok: false; reason: string | null };

// Names the folders whose sample assignment the backend refused, for the toast.
//
// Worth saying plainly rather than folding into the success message: the
// auditor picked that sample by hand and it was not used, so a count alone
// would read as though their choice had been honoured.
function describeDroppedMappings(folders: string[]): string {
  const named = folders.map((folder) => `"${folder}"`).join(", ");
  return folders.length === 1
    ? `${named} was not filed against the sample you picked, because that sample is on a different procedure. Its files are matched on their contents instead.`
    : `${folders.length} folders were not filed against the samples you picked, because those samples are on a different procedure: ${named}. Their files are matched on their contents instead.`;
}

// Sends the faults that are OURS to Sentry, so the dialog's "the Midship team
// has been notified" is true.
//
// It was not. The whole reporting path was a `console.error`, which stays in
// the auditor's browser: Sentry only auto-reports errors that ESCAPE, via
// `window.onerror` and unhandled rejections, and this one is caught. Turning
// console calls into events needs `captureConsoleIntegration`, which
// `Sentry.init` does not register. So the one class of fault we ask auditors
// not to report was also the one nothing was listening for.
function reportParseFault(
  error: unknown,
  kind: "run" | "api" | "unknown",
): void {
  Sentry.captureException(error, {
    tags: { source: "evidence-upload", operation: "parse", kind },
  });
}

/**
 * The four evidence-upload calls for the session the caller is inside.
 *
 * Both entities serve the same four routes; only the prefix differs, so the
 * session kind comes from the audit provider rather than a prop — every caller
 * of this hook renders inside one, and reading it here means a new call site
 * cannot forget to branch.
 */
export function useEvidenceUpload({
  controlSessionId,
}: UseEvidenceUploadOptions) {
  const { sessionKind } = useAudit();
  const sessionBase = controlsV2SessionApiBase(sessionKind, controlSessionId);
  // Identifies the newest parse attempt. Published as state, not just held in
  // a ref, because a caller timing the wait needs to restart when a new
  // attempt begins - and `parseLoading` stays true across a cancel-and-retry.
  const parseAttempt = useRef(0);
  const [parseAttemptId, setParseAttemptId] = useState(0);
  const [parseLoading, setParseLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // The worker's own state, so the dialog can tell "queued" from "reading
  // your archive". Null until the run reports anything.
  const [parseState, setParseState] = useState<EvidenceRunState | null>(null);

  // Resolves with the preview, or with the reason the archive was rejected
  // when the auditor is the one who has to fix it.
  const parseZipFile = useCallback(
    async (
      file: File,
      procedureSessionIds: string[],
      signal?: AbortSignal,
    ): Promise<ParseZipOutcome> => {
      // Every attempt takes a ticket; only the newest may write the shared
      // loading state. Closing mid-parse aborts the caller but not the request
      // in flight, so an abandoned attempt still resolved and cleared
      // `parseLoading` for the one that replaced it, blanking the dialog.
      const attempt = parseAttempt.current + 1;
      parseAttempt.current = attempt;
      setParseAttemptId(attempt);
      const isCurrent = () => parseAttempt.current === attempt;

      setParseLoading(true);
      setParseState(null);
      try {
        // 1. Get a presigned URL for the ZIP file
        const [presigned] = await requestPresignedUrls([
          { filename: file.name, contentType: "application/zip" },
        ]);

        // 2. Upload the ZIP to S3, tracking progress
        setUploadProgress(0);
        await uploadFileToS3(
          file,
          presigned.presignedUrl,
          "application/zip",
          presigned.requiresServerSideEncryption,
          (progress) => setUploadProgress(progress),
          // Otherwise closing the dialog mid-upload left the browser pushing
          // the whole archive to S3 with nobody waiting for it.
          signal,
        );
        setUploadProgress(null);

        // 3. Send upload_id to the parse endpoint instead of the file
        const formData = new FormData();
        formData.append("upload_id", presigned.uploadId);
        formData.append("procedure_session_ids", procedureSessionIds.join(","));

        // 4. Dispatch the parse and poll for its preview. Decompression,
        // preview extraction and structure detection run on a worker, so this
        // request returns as soon as the archive is staged — a large zip no
        // longer holds a connection open past the load balancer's idle timeout
        // and fails in the browser while the parse itself succeeds.
        const dispatch = EvidenceParseDispatchSchema.parse(
          await fetchFastApi({
            path: `${sessionBase}/evidence/parse/async`,
            method: "POST",
            body: formData,
          }),
        );

        const preview = await pollEvidencePreview({
          sessionApiBase: sessionBase,
          uploadId: dispatch.uploadId,
          signal,
          onState: (state) => {
            if (isCurrent()) setParseState(state);
          },
        });

        // Null only means the caller aborted, which it already knows.
        return preview ? { ok: true, preview } : { ok: false, reason: null };
      } catch (error) {
        console.error("Error parsing ZIP file:", error);
        if (
          error instanceof EvidenceRunFailedError ||
          error instanceof EvidenceRunTimeoutError
        ) {
          // Our worker broke or overran, so `reason` stays null and the dialog
          // uses the generic copy. Returning the message here made the dialog
          // say "This ZIP file can't be processed - remove or convert those
          // files", which blames the auditor for a fault they cannot act on.
          reportParseFault(error, "run");
          toast.error(error.message);
          return { ok: false, reason: null };
        }
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
          // A 400 is the auditor's to fix - unsupported types, not a zip,
          // too large. Hand the reason back instead of blaming ourselves.
          //
          // Anything else here is ours, and this is the likeliest way for it
          // to be ours: a 500 from presign or parse-dispatch, a 500 on the
          // preview poll, or five consecutive dropped polls. A null `reason`
          // renders the copy that says the Midship team has been notified, so
          // it has to be reported or that sentence is false again - which is
          // the whole point of this branch's sibling above.
          if (error.status !== 400) {
            reportParseFault(error, "api");
          }
          return {
            ok: false,
            reason: error.status === 400 ? error.getUserMessage() : null,
          };
        }
        if (error instanceof DOMException && error.name === "AbortError") {
          return { ok: false, reason: null };
        }
        reportParseFault(error, "unknown");
        toast.error("Failed to parse ZIP file");
        return { ok: false, reason: null };
      } finally {
        if (isCurrent()) {
          setUploadProgress(null);
          setParseLoading(false);
          setParseState(null);
        }
      }
    },
    [sessionBase],
  );

  // Reports the counts once the worker has created the documents. Detached from
  // the confirm call itself: the dialog closes as soon as the work is
  // dispatched, so this is the only thing still watching, and it may outlive
  // the dialog by minutes. Touches nothing but toasts for that reason.
  const reportConfirmOutcome = useCallback(
    async (uploadId: string) => {
      try {
        const result = await pollEvidenceConfirm({
          sessionApiBase: sessionBase,
          uploadId,
        });
        if (!result) return;

        if (result.status === "completed") {
          // An overridden mapping gets its own message, and it is a warning
          // rather than a success: the auditor assigned that folder to a
          // sample by hand and the backend refused it, because the sample
          // belongs to a procedure this upload does not write to. The files
          // went up and are matched on their contents instead, so a plain
          // success toast would read as "your choice was honoured".
          if (result.droppedMappingFolders.length > 0) {
            toast.warning(
              `Uploaded ${result.createdDocumentCount} documents. ${describeDroppedMappings(result.droppedMappingFolders)}`,
              { duration: 10000 },
            );
          }
          // Skipped files are reported off the count, not off the status.
          // Every backend branch that skips documents still answers
          // `completed`, so the old `partial` branch could not fire and the
          // skipped count never reached the auditor.
          else if (result.skippedDocumentCount > 0) {
            toast.warning(
              `Uploaded ${result.createdDocumentCount} documents, ${result.matchedDocumentCount} matched to samples. ${result.skippedDocumentCount} could not be processed.`,
            );
          } else {
            toast.success(
              `Uploaded ${result.createdDocumentCount} documents, ${result.matchedDocumentCount} matched to samples`,
            );
          }
        } else if (result.status === "no_sessions") {
          // Confirm succeeded and created nothing: no enabled procedure to
          // write to. Green run, zero documents. Unhandled, it was the one
          // outcome that said nothing, so the auditor assumed it worked.
          toast.error(
            "No enabled procedure to upload into. Nothing was imported - enable a procedure on this control and try again.",
          );
        } else {
          // Any status added later. Better blunt than the silence that let
          // `no_sessions` drop an archive unnoticed.
          toast.warning(
            `Upload finished with an unexpected result (${result.status}). ${result.createdDocumentCount} documents created.`,
          );
        }
      } catch (error) {
        console.error("Error waiting on confirmed upload:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else if (error instanceof Error) {
          toast.error(error.message);
        }
      }
    },
    [sessionBase],
  );

  // Resolves to "dispatched" once the worker has the upload, "indeterminate"
  // when the dispatch request itself was killed at the network/gateway level
  // (it may or may not have landed), or null on a genuine failure.
  const confirmUpload = useCallback(
    async (
      uploadId: string,
      pattern: ZipOrganizationPattern,
      procedureSessionId: string | null,
      sampleMappings?: ConfirmSampleMapping[],
      docTypeMappings?: ConfirmDocTypeMapping[],
    ): Promise<"dispatched" | "indeterminate" | null> => {
      setConfirmLoading(true);
      try {
        const body: Record<string, string | object | boolean> = {
          upload_id: uploadId,
          confirmed_pattern: pattern,
        };

        // The backend reads a missing id as "every enabled procedure
        // session", which is the old behaviour. Omitting is a choice, not a
        // requirement: `procedure_session_id: str | None = None` takes an
        // explicit null down the same branch.
        if (procedureSessionId) {
          body.procedure_session_id = procedureSessionId;
        }

        if (pattern === "sample_based" && sampleMappings) {
          body.sample_mappings = sampleMappings;
        } else if (pattern === "doc_type_based" && docTypeMappings) {
          body.doc_type_mappings = docTypeMappings;
        }

        // Persisting and dispatching every file in the archive happens on a
        // worker, so this request returns as soon as the run is queued. It no
        // longer grows with the archive, and so can no longer outlive the load
        // balancer's idle timeout and report a failure for work that succeeded.
        const dispatch = EvidenceConfirmDispatchSchema.parse(
          await fetchFastApi({
            path: `${sessionBase}/evidence/confirm/async`,
            method: "POST",
            body,
          }),
        );

        // Deliberately not awaited: the auditor is unblocked now, and the
        // counts are reported whenever the worker gets to them.
        void reportConfirmOutcome(dispatch.uploadId);

        return "dispatched";
      } catch (error) {
        console.error("Error confirming upload:", error);
        if (
          error instanceof ApiError &&
          (error.isNetworkError || error.status === 502 || error.status === 504)
        ) {
          // The dispatch was killed by the network or a gateway before a real
          // answer arrived, so we cannot tell whether the run was queued. Don't
          // claim failure and don't re-arm the button: a second confirm is what
          // created duplicates before.
          toast.warning(
            "Connection interrupted — your files may still be processing. Status will update on this page.",
          );
          return "indeterminate";
        }
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to process ZIP file");
        }
        return null;
      } finally {
        setConfirmLoading(false);
      }
    },
    [sessionBase, reportConfirmOutcome],
  );

  const refreshMappings = useCallback(
    async (
      uploadId: string,
      procedureSessionId: string,
    ): Promise<EvidenceUploadPreview | null> => {
      setRefreshLoading(true);
      try {
        const response = await fetchFastApi({
          path: `${sessionBase}/evidence/preview/${uploadId}/refresh-mappings`,
          method: "POST",
          body: {
            procedure_session_id: procedureSessionId,
          },
        });

        const parsed = EvidenceUploadPreviewSchema.parse(response);
        return parsed;
      } catch (error) {
        console.error("Error refreshing mappings:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to refresh mappings");
        }
        return null;
      } finally {
        setRefreshLoading(false);
      }
    },
    [sessionBase],
  );

  return {
    parseLoading,
    parseState,
    parseAttemptId,
    confirmLoading,
    refreshLoading,
    uploadProgress,
    parseZipFile,
    confirmUpload,
    refreshMappings,
  };
}
